import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UserStatsCard from './components/UserStatsCard';
import UserFilters from './components/UserFilters';
import BulkActionsBar from './components/BulkActionsBar';
import UserTable from './components/UserTable';
import UserDetailModal from './components/UserDetailModal';
import InviteUserModal from './components/InviteUserModal';

const UserManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Mock user data
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@company.com",
      department: "Engineering",
      role: "Department Manager",
      status: "active",
      lastLogin: new Date(Date.now() - 900000) // 15 minutes ago
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@company.com",
      department: "Engineering",
      role: "Contributor",
      status: "active",
      lastLogin: new Date(Date.now() - 3600000) // 1 hour ago
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@company.com",
      department: "Marketing",
      role: "Contributor",
      status: "active",
      lastLogin: new Date(Date.now() - 7200000) // 2 hours ago
    },
    {
      id: 4,
      name: "David Kim",
      email: "david.kim@company.com",
      department: "Sales",
      role: "Viewer",
      status: "inactive",
      lastLogin: new Date(Date.now() - 86400000) // 1 day ago
    },
    {
      id: 5,
      name: "Lisa Thompson",
      email: "lisa.thompson@company.com",
      department: "HR",
      role: "Department Manager",
      status: "active",
      lastLogin: new Date(Date.now() - 1800000) // 30 minutes ago
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson@company.com",
      department: "Finance",
      role: "Auditor",
      status: "active",
      lastLogin: new Date(Date.now() - 10800000) // 3 hours ago
    },
    {
      id: 7,
      name: "Anna Martinez",
      email: "anna.martinez@company.com",
      department: "Operations",
      role: "Contributor",
      status: "pending",
      lastLogin: null
    },
    {
      id: 8,
      name: "Robert Brown",
      email: "robert.brown@company.com",
      department: "Legal",
      role: "Viewer",
      status: "suspended",
      lastLogin: new Date(Date.now() - 259200000) // 3 days ago
    }
  ]);

  // Filter users based on search and filters
  const filteredUsers = users?.filter(user => {
    const matchesSearch = !searchQuery || 
      user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesDepartment = !selectedDepartment || user?.department === selectedDepartment;
    const matchesRole = !selectedRole || user?.role === selectedRole;
    const matchesStatus = !selectedStatus || user?.status === selectedStatus;

    return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
  });

  // Calculate statistics
  const stats = {
    totalUsers: users?.length,
    activeUsers: users?.filter(u => u?.status === 'active')?.length,
    pendingUsers: users?.filter(u => u?.status === 'pending')?.length,
    departments: [...new Set(users.map(u => u.department))]?.length
  };

  const handleUserSelect = (userId, selected) => {
    if (selected) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers?.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedUsers(filteredUsers?.map(user => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'on users:', selectedUsers);
    // Implement bulk action logic here
    setSelectedUsers([]);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedDepartment('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  const handleInviteUser = (userData) => {
    const newUser = {
      id: users?.length + 1,
      name: userData?.name,
      email: userData?.email,
      department: userData?.department,
      role: userData?.role,
      status: 'pending',
      lastLogin: null
    };
    
    setUsers([...users, newUser]);
    console.log('User invited:', newUser);
  };

  const handleSaveUser = (updatedUser) => {
    setUsers(users?.map(user => 
      user?.id === updatedUser?.id ? updatedUser : user
    ));
    setIsUserModalOpen(false);
    console.log('User updated:', updatedUser);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72'
      }`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">User Management</h1>
              <p className="text-muted-foreground">
                Manage organizational users, roles, and permissions
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Export Users
              </Button>
              
              <Button
                onClick={() => setIsInviteModalOpen(true)}
                iconName="UserPlus"
                iconPosition="left"
                iconSize={16}
              >
                Invite User
              </Button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <UserStatsCard
              title="Total Users"
              value={stats?.totalUsers}
              icon="Users"
              trend="up"
              trendValue="+12%"
              color="primary"
            />
            
            <UserStatsCard
              title="Active Users"
              value={stats?.activeUsers}
              icon="UserCheck"
              trend="up"
              trendValue="+8%"
              color="success"
            />
            
            <UserStatsCard
              title="Pending Invites"
              value={stats?.pendingUsers}
              icon="UserX"
              trend="down"
              trendValue="-3%"
              color="warning"
            />
            
            <UserStatsCard
              title="Departments"
              value={stats?.departments}
              icon="Building"
              trend="neutral"
              trendValue="0%"
              color="accent"
            />
          </div>

          {/* Filters */}
          <UserFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedDepartment={selectedDepartment}
            onDepartmentChange={setSelectedDepartment}
            selectedRole={selectedRole}
            onRoleChange={setSelectedRole}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            onClearFilters={handleClearFilters}
            resultCount={filteredUsers?.length}
          />

          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedUsers?.length}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
          />

          {/* Users Table */}
          <UserTable
            users={filteredUsers}
            selectedUsers={selectedUsers}
            onUserSelect={handleUserSelect}
            onSelectAll={handleSelectAll}
            onUserClick={handleUserClick}
            onBulkAction={handleBulkAction}
          />

          {/* Empty State */}
          {filteredUsers?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedDepartment || selectedRole || selectedStatus
                  ? 'Try adjusting your filters to see more results.' :'Get started by inviting your first user.'}
              </p>
              {!searchQuery && !selectedDepartment && !selectedRole && !selectedStatus && (
                <Button
                  onClick={() => setIsInviteModalOpen(true)}
                  iconName="UserPlus"
                  iconPosition="left"
                  iconSize={16}
                >
                  Invite User
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <UserDetailModal
        user={selectedUser}
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUser}
      />
    </div>
  );
};

export default UserManagement;